import React, { useEffect, useState, useRef } from 'react';
import { HotTable } from "@handsontable/react";
import 'handsontable/styles/handsontable.css';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';
import toast from 'react-hot-toast';
import { HyperFormula } from 'hyperformula';
import AnalystPromptBox from './AnalystPromptBox';

registerAllModules();

const hyperformulaInstance = HyperFormula.buildEmpty({
  licenseKey: 'internal-use-in-handsontable',
});

const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function convertArrayToJSON(dataArray) {
  if (!dataArray || dataArray.length < 2) return [];
  const headers = dataArray[0];
  const data = dataArray.slice(1);

  return data.map(row => {
    const obj = Object.create(null);
    headers.forEach((header, index) => {
      if (header && !DANGEROUS_KEYS.has(header)) {
        obj[header] = row[index];
      }
    });
    return obj;
  });
}

const AnalystSpreadsheet = ({ df, setDf, onSubmit, highlightedRanges, setHighlightedRanges, hotRef, completionLoading, onDataChange, initialData }) => {

  const [promptBox, setPromptBox] = useState({ visible: false, x: 0, y: 0 });
  const [promptText, setPromptText] = useState("");
  const [selectedRanges, setSelectedRanges] = useState([]);
  const containerRef = useRef(null);
  const loadedRef = useRef(false);

  // Load initial data from IndexedDB when the sheet mounts
  useEffect(() => {
    if (!initialData || !hotRef.current?.hotInstance || loadedRef.current) return;
    loadedRef.current = true;
    hotRef.current.hotInstance.loadData(initialData);
  }, [initialData, hotRef]);

  // Keep highlight rendering in sync via updateSettings
  useEffect(() => {
    if (!hotRef.current?.hotInstance) return;
    hotRef.current.hotInstance.updateSettings({
      cells: function (row, col) {
        const cellProperties = {};
        const isHighlighted = highlightedRanges.some(range =>
          row >= range.fromRow && row <= range.toRow &&
          col >= range.fromCol && col <= range.toCol
        );
        if (isHighlighted) {
          cellProperties.renderer = function (instance, td) {
            Handsontable.renderers.TextRenderer.apply(this, arguments);
            td.style.backgroundColor = '#fce4ec';
            td.style.borderBottom = '0.5px solid #f48fb1';
            td.style.borderRight = '0.5px solid #f48fb1';
            td.style.color = '#404880';
          };
        }
        return cellProperties;
      }
    });
    hotRef.current.hotInstance.render();
  }, [highlightedRanges, hotRef]);

  const handleOnSubmit = (menu, submenu, prompt = "") => {
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance) {
      console.error('Failed to access the Handsontable instance.');
      return;
    }

    const ranges = selectedRanges.length > 0 ? selectedRanges : hotInstance.getSelected();
    if (!ranges || ranges.length === 0) {
      toast.error("No data is selected.");
      return;
    }

    let selectedData = [];
    let nonNullCount = 0;
    const headers = hotInstance.getDataAtRow(0);
    let headerCheckPassed = true;

    ranges.forEach(range => {
      const startCol = range[1] === -1 ? 0 : range[1];
      const endCol = range[3] === -1 ? hotInstance.countCols() - 1 : range[3];
      const selectedHeaders = headers.slice(startCol, endCol + 1);
      if (selectedHeaders.some(header => !header)) {
        headerCheckPassed = false;
      }
    });

    if (!headerCheckPassed) {
      toast.error("One or more selected columns do not have headers. Please ensure all selected columns contain headers.");
      return;
    }

    let includeHeaders = true;
    for (let range of ranges) {
      const startRow = range[0] === -1 ? 0 : range[0];
      const endRow = range[2] === -1 ? hotInstance.countRows() - 1 : range[2];
      const startCol = range[1] === -1 ? 0 : range[1];
      const endCol = range[3] === -1 ? hotInstance.countCols() - 1 : range[3];

      if (startRow === 0) {
        includeHeaders = false;
      }

      for (let row = startRow; row <= endRow; row++) {
        const rowData = hotInstance.getDataAtRow(row);
        const selectedRowData = rowData.slice(startCol, endCol + 1);
        selectedData.push(selectedRowData);
        nonNullCount += selectedRowData.filter(value => value != null && value !== "").length;
      }
    }

    if (includeHeaders) {
      selectedData.unshift(headers.slice(ranges[0][1], ranges[0][3] + 1));
    }

    if (nonNullCount < 10) {
      toast.error("Please select at least 10 non-null values.");
      return;
    }

    const jsonData = convertArrayToJSON(selectedData);
    const selectedRangeForAnalysis = ranges.map(range => ({
      fromRow: range[0] === -1 ? 0 : range[0],
      toRow: range[2] === -1 ? hotInstance.countRows() - 1 : range[2],
      fromCol: range[1] === -1 ? 0 : range[1],
      toCol: range[3] === -1 ? hotInstance.countCols() - 1 : range[3]
    }));

    setHighlightedRanges([...selectedRangeForAnalysis]);
    setDf([...jsonData]);
    onSubmit(jsonData, selectedRangeForAnalysis, menu, submenu, prompt);
  };

  const submitPrompt = () => {
    if (!promptText.trim()) {
      toast.error("Prompt text cannot be empty.");
      return;
    }
    handleOnSubmit('ask', null, promptText);
    setPromptBox({ visible: false, x: 0, y: 0 });
    setPromptText("");
    setSelectedRanges([]);
  };

  const customContextMenu = {
    items: {
      "five": {
        key: 'prompt',
        name: 'Ask',
        callback: function (key, options) {
          const { start } = options[0];
          const cell = hotRef.current.hotInstance.getCell(start.row, start.col);
          if (!cell || !containerRef.current) return;

          const cellRect = cell.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          setSelectedRanges(hotRef.current.hotInstance.getSelected());
          setHighlightedRanges([...hotRef.current.hotInstance.getSelected()]);

          setPromptBox({
            visible: true,
            x: cellRect.left - containerRect.left,
            y: cellRect.top - containerRect.top,
          });
        }
      },
      "one": {
        key: "analysis",
        name: 'Analysis',
        submenu: {
          items: [
            { key: 'analysis:1', name: 'Exploratory', callback: () => handleOnSubmit('analysis', 'exploratory') },
            { key: 'analysis:2', name: 'Descriptive', callback: () => handleOnSubmit('analysis', 'descriptive') },
            { key: 'analysis:3', name: 'Executive', callback: () => handleOnSubmit('analysis', 'executive') }
          ]
        }
      },
      "two": {
        key: "report",
        name: 'Report',
        submenu: {
          items: [
            { key: 'report:1', name: 'Financial', callback: () => handleOnSubmit('report', 'financial') },
            { key: 'report:2', name: 'Operational', callback: () => handleOnSubmit('report', 'operational') },
            { key: 'report:3', name: 'Marketing', callback: () => handleOnSubmit('report', 'marketing') },
            { key: 'report:4', name: 'Customer', callback: () => handleOnSubmit('report', 'customer') },
            { key: 'report:5', name: 'Compliance', callback: () => handleOnSubmit('report', 'compliance') }
          ]
        }
      },
      "three": {
        key: "statistics",
        name: 'Statistics',
        submenu: {
          items: [
            { key: 'statistics:1', name: 'Descriptive', callback: () => handleOnSubmit('statistics', 'descriptive') },
            { key: 'statistics:2', name: 'Correlation', callback: () => handleOnSubmit('statistics', 'correlation') },
            { key: 'statistics:3', name: 'Hypothesis', callback: () => handleOnSubmit('statistics', 'hypothesis') },
            { key: 'statistics:4', name: 'Regression', callback: () => handleOnSubmit('statistics', 'regression') }
          ]
        }
      },
      "four": {
        key: "ml",
        name: 'Machine Learning',
        submenu: {
          items: [
            { key: 'ml:1', name: 'Principal Component Analysis', callback: () => handleOnSubmit('ml', 'pca') },
            { key: 'ml:2', name: 'Clustering', callback: () => handleOnSubmit('ml', 'clustering') },
            { key: 'ml:3', name: 'Feature Engineering', callback: () => handleOnSubmit('ml', 'feature_engineering') },
            { key: 'ml:4', name: 'Decision Trees', callback: () => handleOnSubmit('ml', 'decision_trees') }
          ]
        }
      },
      "row_above": {},
      "row_below": {},
      "col_left": {},
      "col_right": {},
      "remove_row": {},
      "remove_col": {},
      "undo": {},
      "redo": {},
      "make_read_only": {},
      "alignment": {},
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <HotTable
        ref={hotRef}
        colHeaders={true}
        rowHeaders={true}
        width="100%"
        contextMenu={customContextMenu}
        colWidths={100}
        minRows={250}
        minCols={15}
        manualColumnResize={true}
        manualRowResize={true}
        columnSorting={false}
        allowInsertRow={true}
        allowRemoveRow={true}
        selectionMode='multiple'
        autoWrapRow={true}
        autoWrapCol={true}
        formulas={{
          engine: HyperFormula,
        }}
        afterChange={(changes, source) => {
          if (!changes || source === 'loadData') return;
          if (onDataChange) onDataChange();
        }}
        afterPaste={() => {
          if (onDataChange) onDataChange();
        }}
        licenseKey="non-commercial-and-evaluation"
      />

      {promptBox.visible && (
        <AnalystPromptBox
          promptBox={promptBox}
          promptText={promptText}
          setPromptText={setPromptText}
          submitPrompt={submitPrompt}
          setPromptBox={setPromptBox}
        />
      )}

      {completionLoading && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-pink"></div>
            <span className="text-sm text-gray-600">Analyzing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalystSpreadsheet;
